// This modal will be used to transfer a del to another user.
import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import styles from "./TransferDelModal.module.css";

const TransferDelModal = ({
  onTransferFunction,
  onCancel,
  employees,
  afterTransfer,
  title = "Transfer DEL",
  message = "Please select a user to transfer the DEL to.",
}) => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef(null);

  // Close when clicking outside the modal
  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onCancel();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onCancel]);

  // Close on escape key
  useEffect(() => {
    function handleEscapeKey(event) {
      if (event.key === "Escape") {
        onCancel();
      }
    }

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [onCancel]);

  // Auto-close modal after success message is shown
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        afterTransfer();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, afterTransfer]);

  const handleTransfer = async () => {
    // Reset previous error and success state
    setError(null);
    setSuccess(null);

    if (!selectedEmployee) {
      setError("Please select a user to transfer the DEL to.");
      return;
    }

    try {
      setIsLoading(true);
      const result = await onTransferFunction(selectedEmployee.value);

      // Check if there was an error in the response
      if (result && result.error) {
        setError(result.error);
        setIsLoading(false);
        return;
      }

      // Success message
      setIsLoading(false);
      setSuccess(`DEL successfully transferred to ${selectedEmployee.label}.`);

      // Don't close the modal immediately - let the user see the success message
      // The useEffect will handle closing after delay
    } catch (err) {
      setIsLoading(false);
      setError(
        err.message ||
          "An error occurred while transferring the DEL. Please try again."
      );
      console.error("Error transferring DEL:", err);
    }
  };
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.transferDelModal} ref={modalRef}>
        <button
          className={styles.closeButton}
          onClick={onCancel}
          aria-label="Close modal"
          disabled={isLoading}
        >
          ×
        </button>
        <h2>{title}</h2>
        <p>{message}</p>

        {error && <div className={styles.errorMessage}>{error}</div>}
        {success && <div className={styles.successMessage}>{success}</div>}

        <Select
          options={employees.map((employee) => ({
            value: employee.employee_id,
            label: employee.label,
          }))}
          onChange={(selectedOption) => {
            setSelectedEmployee(selectedOption);
            setError(null); // Clear error when user makes a selection
            setSuccess(null); // Clear success when user makes a selection
          }}
          value={
            selectedEmployee
              ? {
                  value: selectedEmployee.value,
                  label: selectedEmployee.label,
                }
              : null
          }
          placeholder="Select an employee..."
          isSearchable={true}
          className={styles.select}
          isDisabled={isLoading || success}
        />
        <button
          className={styles.clearButton}
          onClick={() => {
            setSelectedEmployee(null);
            setError(null);
            setSuccess(null);
          }}
          disabled={isLoading || success}
        >
          Clear
        </button>
        <div className={styles.buttonGroup}>
          <button onClick={handleTransfer} disabled={isLoading || success}>
            {isLoading && <span className={styles.loadingIndicator}></span>}
            Transfer
          </button>
          <button onClick={onCancel} disabled={isLoading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferDelModal;
